import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Grid, CircularProgress,
  Chip, Divider, IconButton, FormControl, Select, MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { collectionsAPI } from '../../services/api';

const CollectionHeaderCard = ({ collection, onCollectionUpdate, setSnackbar }) => {
  const [editMode, setEditMode] = useState({
    title: false,
    description: false,
    status: false
  });
  const [formValues, setFormValues] = useState({
    title: '',
    description: '',
    status: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (collection) {
      setFormValues({
        title: collection.title || '',
        description: collection.description || '',
        status: collection.status || 'draft'
      });
    }
  }, [collection]);

  const toggleEditMode = (field) => {
    setEditMode(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
    
    // Reset form value to current value when toggling edit mode
    if (!editMode[field]) {
      setFormValues(prev => ({
        ...prev,
        [field]: collection[field]
      }));
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveField = async (field) => {
    if (!collection) return;
    
    try {
      setLoading(true);
      
      // Create update payload with just the field being updated
      const updateData = { [field]: formValues[field] };
      
      await collectionsAPI.updateCollection(collection.id, updateData);
      
      // Update collection state with new value
      if (onCollectionUpdate) {
        onCollectionUpdate({
          ...collection,
          [field]: formValues[field]
        });
      }
      
      // Exit edit mode
      setEditMode(prev => ({
        ...prev,
        [field]: false
      }));
      
      setSnackbar({
        open: true,
        message: `${field.charAt(0).toUpperCase() + field.slice(1)} updated successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      setSnackbar({
        open: true,
        message: `Failed to update ${field}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!collection) return null;

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Box 
            component="img" 
            src={collection.coverImage} 
            alt={collection.title} 
            sx={{ 
              width: '100%',
              borderRadius: 2,
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://placehold.co/400x600?text=No+Cover';
            }}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          {editMode.title ? (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TextField 
                name="title"
                value={formValues.title} 
                onChange={handleFormChange} 
                variant="outlined" 
                fullWidth
                label="Title"
                sx={{ mr: 2 }}
              />
              <Button 
                variant="contained" 
                startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={() => handleSaveField('title')}
                disabled={loading}
              >
                Save
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
                {collection.title}
              </Typography>
              <IconButton onClick={() => toggleEditMode('title')}>
                <EditIcon />
              </IconButton>
            </Box>
          )}
          
          {editMode.description ? (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <TextField 
                name="description"
                value={formValues.description} 
                onChange={handleFormChange} 
                variant="outlined" 
                fullWidth
                multiline
                rows={4}
                label="Description"
                sx={{ mr: 2 }}
              />
              <Button 
                variant="contained" 
                startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={() => handleSaveField('description')}
                disabled={loading}
                sx={{ mt: 1 }}
              >
                Save
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', mb: 3 }}>
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ flexGrow: 1, whiteSpace: 'pre-wrap' }}
              >
                {collection.description || 'No description available'}
              </Typography>
              <IconButton onClick={() => toggleEditMode('description')}>
                <EditIcon />
              </IconButton>
            </Box>
          )}
          
          <Divider sx={{ my: 2 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  {editMode.status ? (
                    <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                      <Select
                        name="status"
                        value={formValues.status}
                        onChange={handleFormChange}
                        displayEmpty
                      >
                        <MenuItem value="draft">Draft</MenuItem>
                        <MenuItem value="published">Published</MenuItem>
                        <MenuItem value="archived">Archived</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <Chip 
                      label={collection.status} 
                      color={collection.status === 'published' ? 'success' : 'default'}
                      size="small"
                    />
                  )}
                </Box>
                {editMode.status ? (
                  <Button 
                    size="small" 
                    variant="contained" 
                    onClick={() => handleSaveField('status')}
                    disabled={loading}
                    sx={{ ml: 1, mt: 1 }}
                  >
                    Save
                  </Button>
                ) : (
                  <IconButton size="small" onClick={() => toggleEditMode('status')}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Books Count
              </Typography>
              <Typography variant="body2">
                {collection.books?.length || 0} books in this collection
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CollectionHeaderCard;
